import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// tailwind-merge that knows the semantic radius names (rounded-control /
// card / pill), so a caller's `rounded-full` replaces a primitive's
// `rounded-control` instead of both classes shipping.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: [{ rounded: ["control", "card", "pill"] }],
    },
  },
});

/**
 * Tailwind class combinator. `cn("p-2", isActive && "bg-surface-800")`
 * merges conflicting tailwind classes (e.g. `p-2 p-4` → `p-4`) and
 * drops falsy values. Same shape shadcn/ui uses — so if we later
 * import any shadcn components, they'll just work.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatRelative(isoTimestamp: string | undefined | null): string {
  if (!isoTimestamp) return "never";
  const now = Date.now();
  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) return isoTimestamp;
  const diffMs = now - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(isoTimestamp).toLocaleDateString();
}

/**
 * Capital first letter, for enum-ish values rendered inside a `.uppercase`
 * label: Glass shouts them anyway, Earth and Midnight show them as typed, so
 * "running" must reach the DOM as "Running". Display only — never write the
 * result back. Leaves mixed-case words ("iPhone") and identifiers that start
 * with a symbol or digit alone.
 */
export function capFirst(s: string | null | undefined): string {
  const v = s ?? "";
  if (/^[a-z][a-z]*[A-Z]/.test(v)) return v;
  return v.charAt(0).toUpperCase() + v.slice(1);
}
